import { Box, ListItem, Typography, IconButton } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteIcon from '@mui/icons-material/Delete';

/**
 * PromptListItem - Reusable component for rendering draggable prompt items
 * Works for both normal list items and drag preview clone
 *
 * @param {Object} props
 * @param {Object} props.item - The prompt item data
 * @param {number} props.index - Index of the item in the list
 * @param {boolean} props.isClone - Whether this is a drag clone preview
 * @param {Object} props.provided - Provided object from react-beautiful-dnd (innerRef, draggableProps, etc)
 * @param {Function} props.getPromptName - Function to get the prompt name from its ID
 * @param {Function} props.handleRemovePrompt - Function to handle removing the prompt
 * @param {Function} props.handleSelectSequencePrompt - Function to handle selecting a prompt
 * @param {boolean} props.isHighlighted - Whether the item should be highlighted (e.g. newly added)
 */
const PromptListItem = ({
	item,
	index,
	isClone = false,
	provided,
	getPromptName,
	handleRemovePrompt,
	handleSelectSequencePrompt,
	isHighlighted = false,
}) => {
	// Build the style object based on whether this is a clone or not
	const listItemStyle = isClone
		? {
				position: 'fixed',
				top: provided.draggableProps.style?.top,
				left: provided.draggableProps.style?.left,
				width: provided.draggableProps.style?.width,
				height: provided.draggableProps.style?.height,
				zIndex: 5000,
				pointerEvents: 'none',
				...provided.draggableProps.style,
			}
		: {
				position: 'relative',
				cursor: 'pointer',
				background: isHighlighted ? 'rgba(144, 202, 249, 0.2)' : 'transparent',
				transition: 'background 0.5s ease',
				// Add visual separation with a thin border at the bottom
				borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
				'&:last-child': {
					borderBottom: 'none', // No border for the last item
				},
			};

	// Handle click on the list item
	const handleClick = (e) => {
		if (isClone) return; // No interaction for clone

		// Only open drawer if we're not clicking the drag handle or delete button
		if (!e.defaultPrevented && e.target.closest('[data-no-drawer]') === null) {
			handleSelectSequencePrompt(item);
		}
	};

	// Handle delete button click
	const handleDelete = (e) => {
		e.preventDefault();
		e.stopPropagation();
		handleRemovePrompt(index);
	};

	return (
		<ListItem
			ref={provided.innerRef}
			{...provided.draggableProps}
			{...provided.dragHandleProps}
			data-testid={`prompt-draggable-${index}`}
			sx={listItemStyle}
			onClick={isClone ? undefined : handleClick}
		>
			{/* Drag Handle */}
			<Box
				{...provided.dragHandleProps}
				sx={{
					mr: 1,
					display: 'flex',
					alignItems: 'center',
					zIndex: 1,
					position: 'relative',
				}}
				data-no-drawer='true'
			>
				<DragIndicatorIcon fontSize='small' color='action' />
			</Box>

			{/* Prompt Name */}
			{getPromptName(item.prompt_id)}

			{/* Spacer */}
			<Box sx={{ flexGrow: 1 }} />

			{/* Index Number */}
			<Typography
				variant='caption'
				color='text.secondary'
				sx={{
					mr: 2,
					display: { xs: 'none', sm: 'block' },
				}}
			>
				#{index + 1}
			</Typography>

			{/* Delete Button */}
			<IconButton
				edge='end'
				aria-label='delete'
				onClick={handleDelete}
				size='small'
				color='error'
				data-no-drawer='true'
			>
				<DeleteIcon fontSize='small' />
			</IconButton>
		</ListItem>
	);
};

export default PromptListItem;
